import React, { useEffect, useState, Fragment } from 'react';
import { SearchIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { DataTable } from '../components/DataTable';
import { SiteDetailDrawer } from '../components/SiteDetailDrawer';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { HuaweiOrder } from '../lib/supabase';
const LIMIT = 25;

/* =========================
   AUTO ZOOM COMPONENT (FIX)
========================= */
function AutoZoom({
  data,
  searchQuery,
  parseCoord
}: any) {
  const map = useMap();
  useEffect(() => {
    if (!data) return;
    const filtered = data.filter((site: any) => site.Site_ID_NE?.toLowerCase().includes(searchQuery.toLowerCase()));
    if (filtered.length === 0) return;

    // 1 RESULT → FLY TO
    if (filtered.length === 1) {
      const site = filtered[0];
      const lat = parseCoord(site.Lat_NE);
      const lng = parseCoord(site.Long_NE);
      if (lat && lng) {
        map.flyTo([lat, lng], 13, {
          duration: 1.2
        });
      }
      return;
    }

    // MULTI RESULT → FIT BOUNDS
    const bounds: [number, number][] = [];
    filtered.forEach((site: any) => {
      const lat = parseCoord(site.Lat_NE);
      const lng = parseCoord(site.Long_NE);
      if (lat && lng) bounds.push([lat, lng]);
    });
    if (bounds.length > 0) {
      map.fitBounds(bounds, {
        padding: [60, 60]
      });
    }
  }, [searchQuery, data, map]);
  return null;
}
export function SiteManagement() {
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('Site_ID_NE');
  const [sortAscending, setSortAscending] = useState(true);
  const [selectedSite, setSelectedSite] = useState<HuaweiOrder | null>(null);
  const [view, setView] = useState<'table' | 'map'>('table');

  /* =========================
     LEAFLET ICON FIX
  ========================= */
  useEffect(() => {
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
    });
  }, []);

  /* =========================
     DATA TABLE
  ========================= */
  const {
    data,
    count,
    loading,
    error
  } = useSupabaseQuery({
    limit: LIMIT,
    page,
    searchQuery,
    orderBy: {
      column: sortColumn,
      ascending: sortAscending
    }
  });

  /* =========================
     DATA MAP
  ========================= */
  const {
    data: mapData,
    loading: mapLoading
  } = useSupabaseQuery({
    limit: 5000,
    searchQuery
  });
  const totalPages = Math.ceil(count / LIMIT);
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortAscending(!sortAscending);
    } else {
      setSortColumn(column);
      setSortAscending(true);
    }
  };

  /* =========================
     PARSE COORD SAFE
  ========================= */
  const parseCoord = (val: any) => {
    if (!val) return null;
    const cleaned = String(val).replace(',', '.').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  /* =========================
     MARKER NE COLOR BY MITRA
  ========================= */
  const getMarkerIcon = (mitra: string) => {
    let color = '#64748b';
    if (mitra?.toLowerCase().includes('huawei')) color = '#ef4444';else if (mitra?.toLowerCase().includes('aviat')) color = '#22c55e';else if (mitra?.toLowerCase().includes('zte')) color = '#3b82f6';else if (mitra?.toLowerCase().includes('eid')) color = '#9ca3af';
    return L.divIcon({
      html: `<div style="
        width:12px;
        height:12px;
        border-radius:50%;
        background:${color};
        border:2px solid white;
        box-shadow:0 0 6px ${color};
      "></div>`
    });
  };

  /* =========================
     FE MARKER
  ========================= */
  const getFEIcon = () => L.divIcon({
    html: `<div style="
        width:8px;
        height:8px;
        border-radius:50%;
        background:#facc15;
      "></div>`
  });
  const defaultCenter: [number, number] = [-2.5, 118];

  /* =========================
     TABLE COLUMNS
  ========================= */
  const columns = [{
    header: 'Site ID',
    accessor: 'Site_ID_NE' as keyof HuaweiOrder,
    sortable: true,
    render: (val: any) => <span className="text-cyan-400 font-medium" data-id="element-974" data-mp="140ioy9vc9dn5">{val}</span>
  }, {
    header: 'Mitra',
    accessor: 'Mitra' as keyof HuaweiOrder
  }, {
    header: 'Area',
    accessor: 'Area' as keyof HuaweiOrder
  }, {
    header: 'TREG',
    accessor: 'TREG' as keyof HuaweiOrder
  }, {
    header: 'Program',
    accessor: 'Tipe_Site' as keyof HuaweiOrder
  }, {
    header: 'Capacity',
    accessor: 'Kapasitas' as keyof HuaweiOrder
  }, {
    header: 'Freq',
    accessor: 'Frekuensi' as keyof HuaweiOrder
  }, {
    header: 'NMS Status',
    accessor: 'Status_koneksi_NMS' as keyof HuaweiOrder,
    render: (val: any) => <span className={`px-2 py-1 rounded text-xs ${val === 'Active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-400'}`} data-id="element-975" data-mp="3kn729jvxtulv">
          {val || '-'}
        </span>
  }];
  return <div className="space-y-6" data-id="element-976" data-mp="2wx4hloucrqxa">
      <h2 className="text-xl font-bold text-slate-100" data-id="element-977" data-mp="2ww1nz95nbado">
        Site Management
      </h2>

      {/* TOGGLE */}
      <div className="flex justify-between items-center gap-4" data-id="element-978" data-mp="30zhinzy5n0nq">
        <div className="flex gap-2 bg-slate-900 p-1 rounded-lg border border-slate-700" data-id="element-979" data-mp="c7f01429lr0w">
          <button onClick={() => setView('table')} className={`px-3 py-1 text-sm rounded-md ${view === 'table' ? 'bg-cyan-500 text-white' : 'text-slate-400'}`} data-id="element-980" data-mp="g87ag0mvi1yr">
            Table
          </button>
          <button onClick={() => setView('map')} className={`px-3 py-1 text-sm rounded-md ${view === 'map' ? 'bg-cyan-500 text-white' : 'text-slate-400'}`} data-id="element-981" data-mp="2m9t4jaiaalao">
            Map
          </button>
        </div>

        <div className="relative w-full md:w-80" data-id="element-982" data-mp="1pjirptgfk7qb">
          <SearchIcon className="absolute left-3 top-3 text-slate-500" data-id="element-983" data-mp="1pl3prt1ye8sx" />
          <input value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setPage(0);
        }} placeholder="Search Site..." className="bg-slate-900 border border-slate-700 text-slate-200 rounded-lg w-full pl-10 p-2.5" data-id="element-984" data-mp="23b747xulcy7t" />
        </div>
      </div>

      {/* =========================
           MAP SECTION
       ========================= */}
      {view === 'map' ? <div className="glass-panel p-4 rounded-xl" data-id="element-985" data-mp="3m9lmjcttnrox">
          {mapLoading ? <div className="text-center text-slate-400 py-10" data-id="element-986" data-mp="3mbg6antcxdrf">
              Loading map...
            </div> : <MapContainer center={defaultCenter} zoom={5} className="h-[500px]" data-id="element-987" data-mp="3dh1puhx3bx6q">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-988" data-mp="52t67zcjqgqp" />

              {/* AUTO ZOOM CONTROLLER */}
              <AutoZoom data={mapData} searchQuery={searchQuery} parseCoord={parseCoord} data-id="element-989" data-mp="90z8au2fw85a" />

              {mapData.map((site: any, i: number) => {
          const latNE = parseCoord(site.Lat_NE);
          const lngNE = parseCoord(site.Long_NE);
          const latFE = parseCoord(site.Lat_FE);
          const lngFE = parseCoord(site.Long_FE);
          if (!latNE || !lngNE || !latFE || !lngFE) return null;
          return <Fragment key={i} data-id="element-990" data-mp="37hdvjjhmqsoz" data-mpi={i}>
                    {/* LINE */}
                    <Polyline positions={[[latNE, lngNE], [latFE, lngFE]]} pathOptions={{
              color: site.Status_koneksi_NMS === 'Active' ? '#22c55e' : '#ef4444',
              weight: 2,
              opacity: 0.8
            }} data-id="element-991" data-mp="37j76haz9zz2s" />

                    {/* NE */}
                    <Marker position={[latNE, lngNE]} icon={getMarkerIcon(site.Mitra)} eventHandlers={{
              click: () => setSelectedSite(site)
            }} data-id="element-992" data-mp="3gic3udz3pmdd">
                      <Popup data-id="element-993" data-mp="l9pvarm4kmw0">
                        <b data-id="element-994" data-mp="lahsu7za8uox">{site.Site_ID_NE} (NE)</b>
                        <div data-id="element-995" data-mp="lbsxsb4gs8hm">{site.Mitra}</div>
                      </Popup>
                    </Marker>

                    {/* FE */}
                    <Marker position={[latFE, lngFE]} icon={getFEIcon()} data-id="element-996" data-mp="u9v4n7avpvct">
                      <Popup data-id="element-997" data-mp="ucqyajfp1xvg">
                        <b data-id="element-998" data-mp="z9r8n6jktmsd">{site.Site_ID_FE} (FE)</b>
                      </Popup>
                    </Marker>
                  </Fragment>;
        })}
            </MapContainer>}
        </div> : error ? <div data-id="element-999" data-mp="188cnorpuajoe">Error: {error}</div> : loading ? <TableSkeleton data-id="element-1000" data-mp="1d0dy083g60bs" /> : <DataTable data={data} columns={columns} page={page} totalPages={totalPages} onPageChange={setPage} onRowClick={setSelectedSite} onSort={handleSort} data-id="element-1001" data-mp="1d1fif36crz66" />}

      <SiteDetailDrawer site={selectedSite} isOpen={!!selectedSite} onClose={() => setSelectedSite(null)} data-id="element-1002" data-mp="1rxwszs6px9ro" />
    </div>;
}